import { createSlice } from '@reduxjs/toolkit';
import { getCurrentUser, getCurrentUserRole, getCurrentUserPermissions, isAuthValid } from '../utils/authUtils';

const initialState = {
  isAuthenticated: isAuthValid(),
  user: getCurrentUser(),
  role: getCurrentUserRole(),
  permissions: getCurrentUserPermissions(),
  token: localStorage.getItem('token'),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      const { user, token } = action.payload;
      state.isAuthenticated = true;
      state.user = user;
      state.role = user.role;
      state.permissions = user.role?.permissions || [];
      state.token = token;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.role = null;
      state.permissions = [];
      state.token = null;
    },
    updateUser: (state, action) => {
      state.user = action.payload;
      state.role = action.payload.role;
      state.permissions = action.payload.role?.permissions || [];
    },
    refreshAuth: (state) => {
      const user = getCurrentUser();
      const role = getCurrentUserRole();
      const permissions = getCurrentUserPermissions();
      const authenticated = isAuthValid();
      
      state.isAuthenticated = authenticated;
      state.user = user;
      state.role = role;
      state.permissions = permissions;
      state.token = authenticated ? localStorage.getItem('token') : null;
    }
  }
});

export const { loginSuccess, logout, updateUser, refreshAuth } = authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectRole = (state) => state.auth.role;
export const selectPermissions = (state) => state.auth.permissions;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectToken = (state) => state.auth.token;

// Permission selectors - strictly permission-based, no Super Admin bypass
export const selectHasPermission = (permissionName) => (state) => {
  return state.auth.permissions.some(permission => permission.name === permissionName);
};

export const selectHasAnyPermission = (permissionNames) => (state) => {
  return permissionNames.some(permissionName => 
    state.auth.permissions.some(permission => permission.name === permissionName)
  );
};

export const selectHasRole = (roleName) => (state) => {
  return state.auth.role?.name === roleName;
};

export const selectIsSuperAdmin = (state) => {
  return state.auth.role?.name === 'Super Admin';
};

// Module permission selectors - strictly permission-based, no Super Admin bypass
export const selectCanViewModule = (module) => (state) => {
  return state.auth.permissions.some(permission => permission.name === `${module}.view`);
};

export const selectCanAddToModule = (module) => (state) => {
  return state.auth.permissions.some(permission => permission.name === `${module}.add`);
};

export const selectCanEditInModule = (module) => (state) => {
  return state.auth.permissions.some(permission => permission.name === `${module}.edit`);
};

export const selectCanDeleteInModule = (module) => (state) => {
  return state.auth.permissions.some(permission => permission.name === `${module}.delete`);
};

// Dropdown permission selectors - strictly permission-based, no Super Admin bypass
export const selectCanViewUniversityDropdown = (state) => {
  return state.auth.permissions.some(permission => permission.name === 'university.dropdown');
};

export const selectCanViewStudentDropdown = (state) => {
  return state.auth.permissions.some(permission => permission.name === 'student_dropdown');
};

export const selectCanViewUserManagementDropdown = (state) => {
  return state.auth.permissions.some(permission => permission.name === 'user_management.dropdown');
};

// Legacy selector for backward compatibility - now points to selectCanAddToModule
export const selectCanCreateInModule = (module) => (state) => {
  return selectCanAddToModule(module)(state);
};

export default authSlice.reducer; 